import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ReportHtml, Search } from 'tms-lib';
import {
  AddressLabel,
  DespatchSampleLine,
  SalesDespatchNoteDetails,
  SalesDespatchNoteForInvoice,
  SalesDespatchNote,
  SalesDespatchNotesCreated,
  ShipmentDetailMagento,
  SalesDespatchNoteReport,
  SalesDespatchNoteEmailContacts,
  SalesDespatchAnalysis,
} from '../../../models';
import { TmsWebBaseEditCreateService } from '../../base';

@Injectable({
  providedIn: 'root',
})
export class SalesDespatchNoteService extends TmsWebBaseEditCreateService<SalesDespatchNote, SalesDespatchNoteDetails> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('SalesDespatchNote', $localize`Sales Despatch Note`);
  }

  createDespatchNote(ids: string[]): Observable<string[]> {
    return this.httpService.post<string[]>(`${this.apiUrl}/CreateDespatchNote`, { ids });
  }

  getCreatedDespatchNotes(ids: string[]): Observable<SalesDespatchNotesCreated[]> {
    return this.httpService.post<SalesDespatchNotesCreated[]>(`${this.apiUrl}/CreatedSalesDespatchNotes`, { ids });
  }

  getDespatchNotesForManifests(model: Search): Observable<SalesDespatchNote[]> {
    return this.httpService.post<SalesDespatchNote[]>(`${this.apiUrl}/GetSalesDespatchNotesForManifests`, model);
  }

  getDespatchNotesForManifest(
    id: string,
    shipperSupplierId: string,
    includeSelected: boolean,
  ): Observable<SalesDespatchNote[]> {
    return this.httpService.get<SalesDespatchNote[]>(
      `${this.apiUrl}/GetSalesDespatchNotesForManifest?id=${id}&shipperSupplierId=${shipperSupplierId}&includeSelected=${includeSelected}`,
    );
  }

  getSalesDespatchNoteReportHtml(id: string, includeStyles: boolean): Observable<string> {
    return this.httpService
      .get<ReportHtml>(`${this.apiUrl}/SalesDespatchNoteReportHtml/${id}?includeStyles=${includeStyles}`)
      .pipe(map((model: ReportHtml) => model.html));
  }

  getSalesDespatchNoteReport(id: string, html: boolean): Observable<SalesDespatchNoteReport> {
    return this.httpService.get<SalesDespatchNoteReport>(
      `${this.apiUrl}/SalesDespatchNoteReport?id=${id}&html=${html}`,
    );
  }

  getForCreateInvoice(): Observable<SalesDespatchNoteForInvoice[]> {
    return this.httpService.get<SalesDespatchNoteForInvoice[]>(`${this.apiUrl}/GetForCreateInvoice`);
  }

  getSampleLines(salesDespatchNoteId: string): Observable<DespatchSampleLine[]> {
    return this.httpService.get<DespatchSampleLine[]>(
      `${this.apiUrl}/GetSampleLines?salesDespatchNoteId=${salesDespatchNoteId}`,
    );
  }

  getAddressLabelForDespatchNote(salesDespatchNoteId: string): Observable<AddressLabel> {
    return this.httpService.get<AddressLabel>(
      `${this.apiUrl}/GetAddressLabelForDespatchNote?salesDespatchNoteId=${salesDespatchNoteId}`,
    );
  }

  getSalesDespatchNoteEmailContacts(id: string): Observable<SalesDespatchNoteEmailContacts> {
    return this.httpService.get<SalesDespatchNoteEmailContacts>(
      `${this.apiUrl}/SalesDespatchNoteEmailContacts?id=${id}`,
    );
  }

  getSalesDespatchAnalysis(fromDate: Date, toDate: Date): Observable<SalesDespatchAnalysis[]> {
    return this.httpService.get<SalesDespatchAnalysis[]>(
      `${this.apiUrl}/SalesDespatchAnalysis?fromDate=${fromDate.toISOString()}&toDate=${toDate.toISOString()}`,
    );
  }

  updateWebsiteShipments(shipmentDetails: ShipmentDetailMagento[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/UpdateWebsiteShipments`, { shipmentDetails });
  }

  addItemsToManifest(salesManifestId: string, ids: string[]): Observable<any> {
    return this.httpService.post<any>(`${this.apiUrl}/AddItemsToManifest`, { salesManifestId, ids });
  }

  exportFtpCsvMagentoData(): Observable<any> {
    return this.httpService.get<any>(`${this.apiUrl}/FtpCsvMagentoData`);
  }
}
